import { encryptText, randomId } from '@/crypto';
import type { EncryptedFile, NewMessage, Role } from '@/api/types';
import { encryptFile, type FileSource } from '@/lib/files';

/** Who is writing to which bridge: what every ciphertext is bound to, besides the id of its message. */
export interface Writer {
  readonly bridgeId: string;
  readonly from: Role;
}

/** A message id: 22 base64url characters (128 random bits), chosen by the sender because the ciphertext is bound to it. */
export function newMessageId(): string {
  return randomId(22);
}

/** A text ready to post: encrypted for its own id, bridge and side. */
export async function sealText(key: CryptoKey, text: string, writer: Writer): Promise<NewMessage> {
  const id = newMessageId();
  return { id, kind: 'text', body: await encryptText(key, text, { ...writer, messageId: id }) };
}

/** A file ready to post: its header is encrypted now, its chunks when the transport asks for them. */
export function sealFile(key: CryptoKey, file: FileSource, writer: Writer): Promise<EncryptedFile> {
  return encryptFile(key, file, { ...writer, messageId: newMessageId() });
}

const turns = new Map<string, Promise<unknown>>();

/**
 * Runs `task` once everything started before it for this bridge has finished. A message is numbered when it is sealed and the
 * number is given back if it was not accepted; that only works while nothing else has taken a number since, so what seals
 * and posts (a text, a file that takes minutes to upload, a deletion mark) goes one at a time per bridge (docs/FORMAT.md §5).
 */
export function inTurn<T>(bridgeId: string, task: () => Promise<T>): Promise<T> {
  const run = (turns.get(bridgeId) ?? Promise.resolve()).then(task);
  const settled = run.then(
    () => undefined,
    () => undefined,
  );
  turns.set(bridgeId, settled);
  void settled.then(() => {
    if (turns.get(bridgeId) === settled) turns.delete(bridgeId);
  });
  return run;
}
